import { useState, useEffect } from 'react';
import { X, Search, Eye, Users, Globe } from 'lucide-react';
import apiClient from '../utils/axios';

export default function LeadPreviewModal({ templateId, isOpen, onClose, onPreview }) {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchLeads();
      fetchDomains();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = leads.filter(lead =>
        lead.first_name?.toLowerCase().includes(lowercasedSearchTerm) ||
        lead.last_name?.toLowerCase().includes(lowercasedSearchTerm) ||
        lead.email?.toLowerCase().includes(lowercasedSearchTerm) ||
        lead.company_name?.toLowerCase().includes(lowercasedSearchTerm) ||
        lead.job_title?.toLowerCase().includes(lowercasedSearchTerm)
      );
      setFilteredLeads(filtered);
    } else {
      setFilteredLeads(leads);
    }
  }, [searchTerm, leads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/leads?limit=1000');
      const leadsData = response.data.leads || [];
      setLeads(leadsData);
      setFilteredLeads(leadsData);
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Error fetching leads.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await apiClient.get('/domains');
      const domainsData = response.data.domains || [];
      setDomains(domainsData);
      // Set default domain to first healthy domain
      const healthyDomain = domainsData.find(d => d.status === 'healthy') || domainsData[0];
      if (healthyDomain) {
        setSelectedDomain(healthyDomain.domain_name);
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
      // Don't fail if domains can't be fetched
    }
  };

  const handlePreview = async () => {
    if (!selectedLead) {
      alert('Please select a lead to preview.');
      return;
    }

    if (!selectedDomain) {
      alert('Please select a sending domain.');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await apiClient.post(`/templates/${templateId}/preview`, {
        leadId: selectedLead.id,
        domain: selectedDomain
      });

      if (onPreview) {
        onPreview(response.data);
      }
    } catch (error) {
      console.error('Error previewing template:', error);
      alert(error.response?.data?.message || 'Error previewing template.');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Eye size={24} /> Preview Template as Lead
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search leads by name, email, company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={selectedDomain || ''}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="">Select Sending Domain...</option>
                {domains.map((domain) => (
                  <option key={domain.domain_name} value={domain.domain_name}>
                    {domain.domain_name} {domain.status === 'healthy' ? '✓' : domain.status === 'warning' ? '⚠' : '✗'}
                  </option>
                ))}
              </select>
              <Globe size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="border border-gray-200 rounded-md overflow-y-auto" style={{ maxHeight: '400px' }}>
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading leads...</div>
            ) : filteredLeads.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No leads found matching your criteria.</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        selectedLead?.id === lead.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        <input
                          type="radio"
                          checked={selectedLead?.id === lead.id}
                          onChange={() => setSelectedLead(lead)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lead.first_name || ''} {lead.last_name || ''}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{lead.email || '-'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{lead.company_name || '-'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{lead.job_title || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePreview}
            disabled={!selectedLead || previewLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {previewLoading ? (
              <>Loading...</>
            ) : (
              <>
                <Eye size={16} /> Preview
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
